/* Staff Profile — every COE staff member (planner, QA, production, etc.)
 * edits their own profile here.
 *
 * Editable: name, email, specialization, languages (CSV), bio, photo.
 * Read-only: phone (OTP binding), role + staff_type (admin-controlled),
 *            account-created timestamp.
 *
 * Photo upload is identical UX to /doctor/profile: drag-and-drop or click
 * to pick, ≤ 2 MB, JPEG/PNG/WebP only. Backend stores it in object storage
 * and serves via /api/public/staff-avatar/{user_id}.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Camera, FloppyDisk, Spinner, User as UserIcon, ShieldCheck, UploadSimple, Star } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";


// iter101.29 — Dropped "CoE-" prefix per user direction. Labels used in
// admin tables, profile pages, and every user-facing surface. Full form
// (kept identical to sidebar version now — no more short/long split
// since prefix removal made the sidebar form clean already).
const STAFF_TYPE_LABELS = {
    counsellor: "Patient Engagement Specialist",
    technician: "Scanning Tech",
    internal_dentist: "Internal Dentist",
    coe_lead: "Operations Head",
    planner: "Treatment Planner",
    production: "Production Tech",
    sales: "Sales & KAM",
    support: "Triage & Support",
    qa: "Quality Assurance",
};


const EMPTY = {
    name: "",
    email: "",
    specialization: "",
    credentials: "",
    designation: "",
    languages: "",
    bio: "",
    public_bio: "",
};


export default function StaffProfile() {
    const { user, setUser } = useAuth();
    const [form, setForm] = useState(EMPTY);
    const [original, setOriginal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoUrl, setPhotoUrl] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);
    // iter100.122 — Counsellor-only self-service availability toggle.
    // Backend endpoints already exist (see routers/landing_admin.py). We
    // guard the UI behind staff_type='counsellor' so nobody else sees it.
    const isCounsellor = user?.staff_type === "counsellor";
    const [availability, setAvailability] = useState({ is_available_now: false, is_featured: false });
    const [availLoading, setAvailLoading] = useState(false);
    const [availBusy, setAvailBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/staff/profile");
            const next = {
                name: data.name || "",
                email: data.email || "",
                specialization: data.specialization || "",
                credentials: data.credentials || "",
                designation: data.designation || "",
                languages: (data.languages || []).join(", "),
                bio: data.bio || "",
                public_bio: data.public_bio || "",
            };
            setForm(next);
            setOriginal(next);
            setPhotoUrl(data.image || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't load profile");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    // Fetch counsellor availability once — only when this staff is a counsellor.
    useEffect(() => {
        if (!isCounsellor) return;
        let cancelled = false;
        setAvailLoading(true);
        api.get("/me/counsellor/availability")
            .then((r) => { if (!cancelled) setAvailability(r.data || { is_available_now: false, is_featured: false }); })
            .catch(() => { /* fresh counsellors won't have a profile row yet — that's fine, defaults hold */ })
            .finally(() => { if (!cancelled) setAvailLoading(false); });
        return () => { cancelled = true; };
    }, [isCounsellor]);

    const toggleAvailability = async (next) => {
        // Optimistic update so the switch feels instant.
        const prev = availability.is_available_now;
        setAvailability((a) => ({ ...a, is_available_now: next }));
        setAvailBusy(true);
        try {
            await api.patch("/me/counsellor/availability", { is_available_now: next });
            toast.success(next
                ? "You're now visible on the homepage."
                : "You're offline — homepage will show the next available counsellor.");
        } catch (e) {
            setAvailability((a) => ({ ...a, is_available_now: prev }));
            toast.error(e?.response?.data?.detail || "Couldn't update availability.");
        } finally { setAvailBusy(false); }
    };

    const dirty = original && JSON.stringify(form) !== JSON.stringify(original);

    const save = async () => {
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                specialization: form.specialization.trim(),
                credentials: form.credentials.trim(),
                designation: form.designation.trim(),
                bio: form.bio.trim(),
                public_bio: form.public_bio.trim(),
                languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
            };
            const { data } = await api.put("/staff/profile", payload);
            const next = {
                name: data.name || "",
                email: data.email || "",
                specialization: data.specialization || "",
                credentials: data.credentials || "",
                designation: data.designation || "",
                languages: (data.languages || []).join(", "),
                bio: data.bio || "",
                public_bio: data.public_bio || "",
            };
            setForm(next);
            setOriginal(next);
            if (setUser && user) {
                setUser({ ...user, name: data.name || user.name, email: data.email || user.email });
            }
            toast.success("Profile updated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't save");
        } finally {
            setBusy(false);
        }
    };

    const uploadPhoto = async (file) => {
        if (!file) return;
        if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
            toast.error("Use JPEG, PNG or WebP");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Image too large (max 2 MB)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/staff/profile/photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPhotoUrl(data.image || "");
            // Reflect the new avatar in AuthContext so the sidebar profile
            // card and any other surface bound to user.image refreshes
            // without a full reload.
            if (setUser && user) {
                setUser({ ...user, image: data.image || "" });
            }
            toast.success("Photo updated");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Couldn't upload");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        if (f) uploadPhoto(f);
    };

    if (loading) {
        return (
            <div className="text-center text-muted-foreground py-16 text-sm" data-testid="staff-profile-loading">
                <Spinner size={20} className="animate-spin mx-auto mb-3 opacity-60" />
                Loading profile…
            </div>
        );
    }

    const initials = (form.name || "?")
        .split(/\s+/).filter(Boolean).slice(0, 2)
        .map((w) => w[0]?.toUpperCase()).join("") || "?";
    const photoHref = photoUrl ? photoUrl : null;
    const roleLabel = STAFF_TYPE_LABELS[user?.staff_type] || "Team member";

    return (
        <div data-testid="staff-profile" className="max-w-5xl pb-24 space-y-5">
            {/* HEADER — iter101.29 · Matches Doctor Profile page grammar
                (text-2xl H1 + text-xs subtitle · no overline eyebrow). */}
            <div>
                <h1 className="font-display text-2xl font-bold tracking-tight">My Profile</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    How you appear inside the Invisique platform. Doctors and patients see you as
                    &quot;Invisique™ Team — {roleLabel}&quot; · your real name is visible only to admins and other Invisique™ staff.
                </p>
            </div>

            {/* PROFILE HERO — iter101.29 · Replicates the Doctor Profile
                hero card: big drag-drop avatar (left) + identity strip
                with name / role chip / phone (right). Same rounded card
                shell so both portals share the exact same aesthetic. */}
            <div className="rounded-2xl border border-border bg-card p-6 flex flex-col md:flex-row gap-6 items-start" data-testid="profile-hero">
                {/* Avatar with drag-drop uploader */}
                <div className="flex flex-col items-center gap-2 shrink-0">
                    <label
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={onDrop}
                        className={`group relative cursor-pointer w-24 h-24 rounded-full overflow-hidden flex items-center justify-center border-2 border-dashed transition ${
                            dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-primary/5"
                        }`}
                        style={photoHref ? { background: "transparent" } : { background: "linear-gradient(135deg, hsl(280 60% 55%), hsl(200 70% 50%))" }}
                        data-testid="staff-profile-photo"
                    >
                        {photoHref ? (
                            <img src={photoHref} alt={form.name} className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-white text-2xl font-display font-bold">{initials}</span>
                        )}
                        {uploading && (
                            <div className="absolute inset-0 bg-black/40 grid place-items-center text-white">
                                <Spinner size={22} className="animate-spin" />
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                            <span className="text-white text-[10px] font-bold tracking-wider uppercase flex items-center gap-1">
                                <UploadSimple size={11} weight="bold" /> Change
                            </span>
                        </div>
                        <input
                            type="file"
                            ref={fileRef}
                            onChange={(e) => uploadPhoto(e.target.files?.[0])}
                            accept="image/jpeg,image/png,image/webp"
                            className="hidden"
                            data-testid="staff-profile-photo-input"
                        />
                    </label>
                    <div className="text-[10px] text-muted-foreground text-center max-w-[110px] leading-tight">
                        Click or drag a photo.<br/>JPG/PNG/WebP, max 2 MB.
                    </div>
                </div>

                {/* Identity strip */}
                <div className="flex-1 min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="font-display text-xl font-bold tracking-tight truncate">{form.name || "Your name"}</h2>
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase font-bold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded">
                            <ShieldCheck size={10} weight="fill" /> {roleLabel}
                        </span>
                    </div>
                    {/* iter101.39 — For counsellors show `Designation` +
                        `Credentials`; other staff types still fall back to
                        the free-text Specialization field. */}
                    {isCounsellor
                        ? (form.designation || form.credentials) && (
                            <div className="text-sm text-primary font-medium inline-flex items-center gap-1.5 flex-wrap">
                                <ShieldCheck size={13} weight="duotone" />
                                {form.designation && <span>{form.designation}</span>}
                                {form.designation && form.credentials && <span className="text-muted-foreground/60">·</span>}
                                {form.credentials && <span className="text-muted-foreground">{form.credentials}</span>}
                            </div>
                        )
                        : form.specialization && (
                            <div className="text-sm text-primary font-medium inline-flex items-center gap-1.5">
                                <ShieldCheck size={13} weight="duotone" /> {form.specialization}
                            </div>
                        )
                    }
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5 pt-1">
                        {user?.phone && <span>{user.phone}</span>}
                        {form.email && <span>{form.email}</span>}
                    </div>
                    {/* iter101.30 — Language chips. Was a comma-string
                        buried in the form; surfacing here so admins
                        routing multilingual doctors can match at a
                        glance, and so planners visibly signal their
                        language coverage. */}
                    {form.languages && form.languages.trim() && (
                        <div className="flex flex-wrap gap-1 pt-1" data-testid="staff-profile-language-chips">
                            {form.languages.split(",").map((lang) => lang.trim()).filter(Boolean).map((lang) => (
                                <span
                                    key={lang}
                                    className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-semibold border border-primary/20"
                                    data-testid={`staff-profile-language-chip-${lang.toLowerCase()}`}
                                >
                                    {lang}
                                </span>
                            ))}
                        </div>
                    )}
                    {/* iter101.39 — Prefer the public bio (what patients see
                        on the homepage) when it's set; fall back to the
                        internal note so the hero card isn't empty. */}
                    {(isCounsellor ? form.public_bio : form.bio) && (
                        <div className="text-xs text-muted-foreground mt-2 line-clamp-2">{isCounsellor ? form.public_bio : form.bio}</div>
                    )}
                </div>
            </div>

            {/* iter100.122 — Counsellor-only: self-service availability
                for the homepage lead-capture card. Backend enforces
                staff_type='counsellor' — we mirror the check client-side
                so non-counsellors never see this section.
                iter101.29 — Lifted out of the identity card so it's its
                own visible surface after the hero. */}
            {isCounsellor && (
                <section className="rounded-2xl border border-border bg-card p-5 space-y-2" data-testid="counsellor-availability-card">
                    <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Homepage availability</div>
                            <div className="mt-1 text-[13px] font-semibold flex items-center gap-1.5 flex-wrap">
                                <span className={availability.is_available_now ? "text-emerald-700" : "text-muted-foreground"} data-testid="counsellor-availability-status">
                                    {availability.is_available_now ? "You're online" : "You're offline"}
                                </span>
                                {availability.is_featured && (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider" data-testid="counsellor-featured-badge">
                                        <Star size={9} weight="fill" /> Featured
                                    </span>
                                )}
                            </div>
                        </div>
                        <Switch
                            checked={availability.is_available_now}
                            onCheckedChange={toggleAvailability}
                            disabled={availLoading || availBusy}
                            data-testid="counsellor-availability-toggle"
                            aria-label="Toggle homepage availability"
                        />
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-snug">
                        {availability.is_featured
                            ? "You're the featured counsellor on the homepage lead card. When you flip offline, the card falls back to the next available counsellor."
                            : "Toggle on when you're at your desk and ready to take callbacks — you'll appear on the homepage lead card if you're the featured counsellor."}
                    </p>
                </section>
            )}

            {/* Editable fields */}
            <section className="bg-card border border-border rounded-2xl p-5 space-y-5" data-testid="staff-profile-fields">
                <div className="flex items-center gap-2">
                    <UserIcon size={16} weight="duotone" className="text-primary" />
                    <h3 className="font-display text-sm font-bold tracking-[0.16em] uppercase">Details</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="name">Full name</Label>
                        <Input
                            id="name"
                            value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                            placeholder="Your full name"
                            data-testid="staff-profile-name"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                            placeholder="you@invisique.com"
                            data-testid="staff-profile-email"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                            id="phone"
                            value={user?.phone || ""}
                            disabled
                            data-testid="staff-profile-phone"
                        />
                        <p className="text-[10px] text-muted-foreground leading-snug">
                            Login binding — only an admin can change this.
                        </p>
                    </div>
                    {/* iter101.39 — Counsellors get Credentials + Designation
                        (matching what the public homepage card actually
                        displays); every other staff type keeps the legacy
                        free-text Specialization. */}
                    {isCounsellor ? (
                        <>
                            <div className="space-y-1.5">
                                <Label htmlFor="credentials">Credentials</Label>
                                <Input
                                    id="credentials"
                                    value={form.credentials}
                                    onChange={(e) => setForm((f) => ({ ...f, credentials: e.target.value }))}
                                    placeholder="e.g. BDS, MDS Ortho"
                                    data-testid="staff-profile-credentials"
                                />
                                <p className="text-[10px] text-muted-foreground leading-snug">
                                    Shown on the homepage card next to your name.
                                </p>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="designation">Designation</Label>
                                <Input
                                    id="designation"
                                    value={form.designation}
                                    onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                                    placeholder="e.g. Senior Patient Engagement Specialist"
                                    data-testid="staff-profile-designation"
                                />
                                <p className="text-[10px] text-muted-foreground leading-snug">
                                    Your job title on the homepage card.
                                </p>
                            </div>
                        </>
                    ) : (
                        <div className="space-y-1.5">
                            <Label htmlFor="specialization">Specialization</Label>
                            <Input
                                id="specialization"
                                value={form.specialization}
                                onChange={(e) => setForm((f) => ({ ...f, specialization: e.target.value }))}
                                placeholder="e.g. Treatment Planning · Class II/III"
                                data-testid="staff-profile-specialization"
                            />
                        </div>
                    )}
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="languages">Languages (comma-separated)</Label>
                        <Input
                            id="languages"
                            value={form.languages}
                            onChange={(e) => setForm((f) => ({ ...f, languages: e.target.value }))}
                            placeholder="English, Hindi, Marathi"
                            data-testid="staff-profile-languages"
                        />
                        {isCounsellor && (
                            <p className="text-[10px] text-muted-foreground leading-snug">
                                Shown as chips on the homepage card so leads know which languages you speak.
                            </p>
                        )}
                    </div>
                    {/* iter101.39 — Split "Bio" into two clearly labelled
                        fields for counsellors: an internal note (teammates
                        only) and the public bio (homepage). Non-counsellor
                        staff keep the single legacy bio field. */}
                    {isCounsellor && (
                        <div className="space-y-1.5 sm:col-span-2">
                            <Label htmlFor="public_bio">Public bio (homepage card)</Label>
                            <Textarea
                                id="public_bio"
                                value={form.public_bio}
                                onChange={(e) => setForm((f) => ({ ...f, public_bio: e.target.value }))}
                                rows={3}
                                placeholder="A short blurb leads see on the homepage — e.g. 12+ years helping patients discover the smile they deserve."
                                data-testid="staff-profile-public-bio"
                            />
                            <p className="text-[10px] text-muted-foreground leading-snug">
                                {form.public_bio.length}/500 characters. Public — shows on the homepage card.
                            </p>
                        </div>
                    )}
                    <div className="space-y-1.5 sm:col-span-2">
                        <Label htmlFor="bio">{isCounsellor ? "Internal note (teammates only)" : "Short bio"}</Label>
                        <Textarea
                            id="bio"
                            value={form.bio}
                            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                            rows={4}
                            placeholder={isCounsellor
                                ? "Private context for admins and teammates — never shown to patients."
                                : "A line or two about you for internal teammates."}
                            data-testid="staff-profile-bio"
                        />
                        <p className="text-[10px] text-muted-foreground leading-snug">
                            {form.bio.length}/600 characters. {isCounsellor && "Private — never shown to patients or leads."}
                        </p>
                    </div>
                </div>
                <div className="flex justify-end gap-2 border-t border-border pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={load}
                        disabled={busy || !dirty}
                        data-testid="staff-profile-reset"
                    >
                        Reset
                    </Button>
                    <Button
                        type="button"
                        onClick={save}
                        disabled={busy || !dirty}
                        data-testid="staff-profile-save"
                    >
                        {busy ? <Spinner size={14} className="animate-spin mr-1.5" /> : <FloppyDisk size={14} className="mr-1.5" />}
                        Save changes
                    </Button>
                </div>
            </section>
        </div>
    );
}
